import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, RefreshCw, ShieldCheck, FileText } from "lucide-react";

// Compliance History — Root Admin only. The backend cloaks /vault/* as 404 for
// every other account, so a non-root admin who guesses the URL sees nothing.
export default function ComplianceVault() {
  const [state, setState] = useState({ loading: true, allowed: false });
  const [status, setStatus] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/vault/status"),
        api.get("/vault/artifacts"),
      ]);
      setStatus(s.data);
      setArtifacts(a.data.artifacts || []);
      setState({ loading: false, allowed: true });
    } catch {
      setState({ loading: false, allowed: false });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const view = async (id) => {
    setOpen({ loading: true, id });
    try {
      const { data } = await api.get(`/vault/artifacts/${id}`);
      // Ignore a late response if the viewer was closed or another artifact
      // was requested meanwhile — otherwise the overlay re-opens and locks
      // out every pointer event on the page.
      setOpen((cur) => (cur && cur.id === id ? { ...data, id } : cur));
    } catch (e) {
      setOpen((cur) => (cur && cur.id === id ? null : cur));
      toast.error(formatApiError(e));
    }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vault/regenerate-blueprints", {});
      toast.success(`${data.filed.length} artifact(s) regenerated from the live engine`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (state.loading) return <div className="font-mono text-sm p-6">LOADING…</div>;
  if (!state.allowed) {
    return (
      <div className="font-mono p-6" data-testid="vault-not-found">
        <h1 className="text-4xl font-black">404</h1>
        <p className="text-sm mt-2">Not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="compliance-vault">
      <div className="border-2 border-ink p-6 bg-secondary/40">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black flex items-center gap-3">
              <Lock className="w-7 h-7" /> COMPLIANCE HISTORY
            </h1>
            <p className="text-sm mt-2 max-w-2xl">
              Foundational compliance artifacts generated from the live tax engine and
              encrypted at rest. Visible only to the Root Admin.
            </p>
          </div>
          <button
            onClick={regenerate}
            disabled={busy}
            data-testid="vault-regenerate-btn"
            className="border-2 border-ink px-4 py-2 font-mono text-xs uppercase bg-white hover:bg-ink hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Regenerate
          </button>
        </div>
        {status && (
          <div className="mt-5 grid gap-2 font-mono text-xs" data-testid="vault-status">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" />{status.encryption}</div>
            <div>ROOT ADMIN · {status.root_admin}</div>
            <div>ARTIFACTS · {status.artifact_count}{status.last_filed_at ? ` · last filed ${String(status.last_filed_at).slice(0, 16).replace("T", " ")} UTC` : ""}</div>
            <div>{status.visibility}</div>
          </div>
        )}
      </div>

      <div className="space-y-4" data-testid="vault-artifact-list">
        {artifacts.length === 0 && (
          <p className="font-mono text-sm" data-testid="vault-empty">
            No artifacts filed yet — use REGENERATE to build them from the live engine.
          </p>
        )}
        {artifacts.map((a) => (
          <div key={a.id} className="border-2 border-ink p-5 bg-white" data-testid={`vault-artifact-${a.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-base md:text-lg font-black flex items-center gap-2">
                  <FileText className="w-4 h-4" /> {a.title}
                </h2>
                <p className="font-mono text-xs mt-1">{a.category} · {a.bytes} bytes · sha256 {String(a.sha256).slice(0, 16)}…</p>
                <p className="font-mono text-xs mt-1 opacity-70">{a.note}</p>
              </div>
              <button
                onClick={() => view(a.id)}
                data-testid={`vault-open-${a.id}`}
                className="border-2 border-ink px-3 py-2 font-mono text-xs uppercase hover:bg-ink hover:text-white transition-colors"
              >
                Decrypt & view
              </button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="vault-viewer">
          <div className="bg-white border-2 border-ink max-w-4xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between border-b-2 border-ink p-4">
              <h3 className="font-black text-base md:text-lg">{open.title || "LOADING…"}</h3>
              <button onClick={() => setOpen(null)} data-testid="vault-viewer-close"
                className="border-2 border-ink px-3 py-1 font-mono text-xs uppercase">Close</button>
            </div>
            <pre className="p-4 overflow-auto text-xs font-mono whitespace-pre-wrap" data-testid="vault-viewer-body">
              {open.loading ? "DECRYPTING…" : open.body}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
